import { NavLink } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useUIStore } from "@/stores/ui";
import { cn } from "@/lib/utils";

interface NavItem {
  label: string;
  icon: string;
  path: string;
  badge?: number;
}

const mainNavItems: NavItem[] = [
  {
    label: "ประกาศจัดซื้อจัดจ้าง",
    icon: "solar:document-text-line-duotone",
    path: "/tenders",
  },
  {
    label: "บริษัท",
    icon: "solar:buildings-2-line-duotone",
    path: "/companies",
  },
  {
    label: "หน่วยงาน",
    icon: "solar:buildings-line-duotone",
    path: "/agencies",
  },
  { label: "แท็ก", icon: "solar:tag-line-duotone", path: "/tags" },
  {
    label: "รายการที่บันทึก",
    icon: "solar:bookmark-line-duotone",
    path: "/watchlist",
  },
];

const proNavItems: NavItem[] = [
  {
    label: "สัญญา",
    icon: "solar:document-add-line-duotone",
    path: "/pro/contracts",
  },
  {
    label: "ผลการจัดซื้อจัดจ้าง",
    icon: "solar:medal-ribbon-line-duotone",
    path: "/pro/awards",
  },
  {
    label: "ข้อเสนอ",
    icon: "solar:hand-money-line-duotone",
    path: "/pro/bids",
  },
  {
    label: "เอกสารแนบ",
    icon: "solar:paperclip-line-duotone",
    path: "/pro/attachments",
  },
  {
    label: "รอบการดึงข้อมูล",
    icon: "solar:refresh-circle-line-duotone",
    path: "/pro/crawl-runs",
  },
  {
    label: "งาน OCR",
    icon: "solar:scanner-line-duotone",
    path: "/pro/ocr",
  },
  {
    label: "บทสรุปผู้บริหาร",
    icon: "solar:checklist-line-duotone",
    path: "/pro/rfq",
  },
];

const hiddenProNavPaths = new Set([
  "/pro/attachments",
  "/pro/crawl-runs",
  "/pro/ocr",
]);

export function SideNav() {
  const { sidebarOpen, closeSidebar } = useUIStore();
  const visibleProNavItems = proNavItems.filter(
    (item) => !hiddenProNavPaths.has(item.path),
  );

  return (
    <>
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:sticky top-0 lg:top-16 left-0 z-50 lg:z-0",
          "h-screen lg:h-[calc(100vh-4rem)]",
          "w-64 bg-white border-r border-slate-200",
          "transition-transform duration-300 ease-out",
          "flex flex-col font-display",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        {/* Mobile header */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <img
              src="/logo.jpg"
              alt="G-Wiz"
              className="w-8 h-8 rounded-lg object-cover"
            />
            <span className="font-display font-bold text-xl text-slate-900">
              G-Wiz
            </span>
          </div>
          <button
            onClick={closeSidebar}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <Icon
              icon="solar:close-circle-line-duotone"
              className="w-5 h-5 text-slate-400"
            />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {/* Main nav */}
          <div className="space-y-1">
            {mainNavItems.map((item) => (
              <NavItem key={item.path} item={item} onClick={closeSidebar} />
            ))}
          </div>

          {/* Advanced nav */}
          <div className="mt-6 mb-2 px-3">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              ระบบเพิ่มเติม
            </p>
          </div>
          <div className="space-y-1">
            {visibleProNavItems.map((item) => (
              <NavItem key={item.path} item={item} onClick={closeSidebar} />
            ))}
          </div>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100">
          <div className="px-3 py-2 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2 text-slate-500">
              <Icon icon="solar:info-circle-line-duotone" className="w-4 h-4" />
              <span className="text-xs">
                โหมดรวม
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-400">
              แสดงทุกฟังก์ชันและข้อมูลในหน้าเดียว
            </p>
          </div>
        </div>
      </aside>
    </>
  );
}

function NavItem({ item, onClick }: { item: NavItem; onClick?: () => void }) {
  return (
    <NavLink
      to={item.path}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
          "text-sm font-medium",
          isActive
            ? "bg-blue-50 text-blue-700"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            icon={item.icon}
            className={cn(
              "w-5 h-5",
              isActive ? "text-blue-600" : "text-slate-400",
            )}
          />
          <span className="flex-1">{item.label}</span>
          {item.badge !== undefined && (
            <span
              className={cn(
                "px-2 py-0.5 text-xs font-medium rounded-full",
                isActive
                  ? "bg-blue-100 text-blue-700"
                  : "bg-slate-100 text-slate-600",
              )}
            >
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
